import axios from "axios";

const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const DB_NAME = "ForecastApiCacheDB";
const STORE_NAME = "api_responses";
const DB_VERSION = 1;
// Bump this whenever a backend filter/query fix would otherwise be masked by a
// stale cached response for up to 24h — it forces old IndexedDB entries to miss.
const CACHE_SCHEMA_VERSION = "v2";

function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "id" });
            }
        };
        request.onsuccess = (e) => resolve(e.target.result);
        request.onerror = (e) => reject(e.target.error);
    });
}

async function getFromIDB(key) {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readonly");
            const store = tx.objectStore(STORE_NAME);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    } catch (e) {
        console.warn("IndexedDB read error", e);
        return null;
    }
}

async function setToIDB(key, payload) {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readwrite");
            const store = tx.objectStore(STORE_NAME);
            const req = store.put({ id: key, payload, timestamp: Date.now() });
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (e) {
        console.warn("IndexedDB write error", e);
    }
}

async function executeWithCache(cacheKey, axiosConfig) {
    // 1. Try to read from IndexedDB
    const cachedObj = await getFromIDB(cacheKey);
    if (cachedObj) {
        const now = Date.now();
        if (now - cachedObj.timestamp < CACHE_DURATION_MS) {
            return { data: cachedObj.payload, fromCache: true };
        }
    }

    // 2. Cache miss or expired, fetch fresh data
    const response = await axios(axiosConfig);

    // 3. Save to IndexedDB if successful
    if (response.data && response.data.status === "success") {
        await setToIDB(cacheKey, response.data);
    }

    return response;
}

export async function getWithCache(url, config = {}) {
    const cacheKey = `cache_${CACHE_SCHEMA_VERSION}_GET_${url}_${JSON.stringify(config.params || {})}`;
    return executeWithCache(cacheKey, { method: "GET", url, ...config });
}

export async function getFresh(url, config = {}) {
    return axios({ method: "GET", url, ...config });
}

export async function postWithCache(url, data, config = {}) {
    const cacheKey = `cache_${CACHE_SCHEMA_VERSION}_POST_${url}_${JSON.stringify(data || {})}_${JSON.stringify(config.params || {})}`;
    return executeWithCache(cacheKey, { method: "POST", url, data, ...config });
}
